import { useState, useEffect } from 'react'
import { supabase } from './supabaseClient'
import EditableCell from './EditableCell'

const statusOptions = ['In Progress', 'Draft', 'Review', 'Complete']

export default function Projects({ session, theme, canEdit = true }) {
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [sortField, setSortField] = useState('due_date')
  const [sortDir, setSortDir] = useState('asc')
  const [form, setForm] = useState({
    title: '', client: '', status: 'In Progress',
    due_date: '', value: '', notes: ''
  })

  const statusColors = {
    'In Progress': { bg: theme.statusGreenBg,  text: theme.statusGreenText  },
    'Draft':       { bg: theme.statusBlueBg,   text: theme.statusBlueText   },
    'Review':      { bg: theme.statusYellowBg, text: theme.statusYellowText },
    'Complete':    { bg: theme.statusGrayBg,   text: theme.statusGrayText   },
  }

  useEffect(() => {
    if (session?.user?.id) fetchProjects()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id])

  const fetchProjects = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('projects').select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false })
    if (!error) setProjects(data)
    setLoading(false)
  }

  const handleSave = async () => {
    if (!form.title) return
    setSaving(true)
    const { error } = await supabase.from('projects')
      .insert([{ ...form, user_id: session.user.id, value: form.value || 0, due_date: form.due_date || null }])
    if (!error) {
      setForm({ title: '', client: '', status: 'In Progress', due_date: '', value: '', notes: '' })
      setShowForm(false)
      fetchProjects()
    }
    setSaving(false)
  }

  const handleUpdate = async (id, field, value) => {
    await supabase.from('projects').update({ [field]: value || null }).eq('id', id)
    await fetchProjects()
  }

  const handleDelete = async (id) => {
    const { error } = await supabase.from('projects').delete().eq('id', id).eq('user_id', session.user.id)
    if (!error) fetchProjects()
  }

  const inputStyle = {
    width: '100%', background: theme.surfaceAlt, border: `1px solid ${theme.border}`,
    color: theme.text, padding: '9px 12px', fontSize: 13,
    fontFamily: "'Nunito Sans', sans-serif", boxSizing: 'border-box', outline: 'none',
  }

  const labelStyle = {
    fontSize: 10, color: theme.textSecondary, marginBottom: 5,
    letterSpacing: '0.1em', fontFamily: "'Open Sans', sans-serif", textTransform: 'uppercase',
  }

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc')
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }

  const sortedProjects = [...projects].sort((a, b) => {
    let aVal = a[sortField]
    let bVal = b[sortField]
    if (aVal == null && bVal == null) return 0
    if (aVal == null) return 1
    if (bVal == null) return -1
    if (sortField === 'due_date') {
      aVal = new Date(aVal); bVal = new Date(bVal)
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal
    }
    if (sortField === 'value') {
      aVal = Number(aVal); bVal = Number(bVal)
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal
    }
    aVal = String(aVal).toLowerCase(); bVal = String(bVal).toLowerCase()
    if (aVal < bVal) return sortDir === 'desc' ? 1 : -1
    if (aVal > bVal) return sortDir === 'desc' ? -1 : 1
    return 0
  })

  const pipelineValue = projects
    .filter(p => p.status !== 'Complete')
    .reduce((sum, p) => sum + (Number(p.value) || 0), 0)

  return (
    <div>
      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Total Projects', value: projects.length },
          { label: 'Active',         value: projects.filter(p => p.status === 'In Progress').length },
          { label: 'In Review',      value: projects.filter(p => p.status === 'Review').length },
          { label: 'Pipeline Value', value: `$${pipelineValue.toLocaleString()}` },
        ].map((stat, i) => (
          <div key={i} style={{ background: theme.surface, border: `1px solid ${theme.border}`, padding: '18px 20px' }}>
            <div style={{ fontSize: 10, color: theme.textMuted, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", marginBottom: 8 }}>{stat.label}</div>
            <div style={{ fontSize: 26, color: theme.text, fontFamily: "'Cardo', serif" }}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: theme.textMuted, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif" }}>
          {projects.length} {projects.length === 1 ? 'project' : 'projects'}
        </div>
        {canEdit && (
          <button onClick={() => setShowForm(!showForm)} style={{
            background: theme.accent, color: '#0a2342', border: 'none',
            padding: '8px 18px', fontSize: 11, letterSpacing: '0.1em',
            textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", fontWeight: 700, cursor: 'pointer',
          }}>{showForm ? 'Cancel' : '+ New Project'}</button>
        )}
      </div>

      {/* New project form */}
      {showForm && (
        <div style={{ background: theme.surface, border: `1px solid ${theme.accent}44`, padding: '24px', marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: theme.accent, letterSpacing: '0.15em', textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", marginBottom: 20 }}>New Project</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div><div style={labelStyle}>Project Title *</div>
              <input style={inputStyle} placeholder="e.g. Q1 Case Study" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
            <div><div style={labelStyle}>Client</div>
              <input style={inputStyle} placeholder="e.g. Acme Corp" value={form.client} onChange={e => setForm({ ...form, client: e.target.value })} /></div>
            <div><div style={labelStyle}>Status</div>
              <select style={inputStyle} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                {statusOptions.map(s => <option key={s}>{s}</option>)}</select></div>
            <div><div style={labelStyle}>Due Date</div>
              <input type="date" style={inputStyle} value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></div>
            <div><div style={labelStyle}>Project Value ($)</div>
              <input type="number" style={inputStyle} placeholder="e.g. 1200" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} /></div>
            <div><div style={labelStyle}>Notes</div>
              <input style={inputStyle} placeholder="Any quick notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <button onClick={handleSave} disabled={saving || !form.title} style={{
            background: form.title ? theme.accent : theme.border, color: '#0a2342', border: 'none',
            padding: '9px 22px', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
            fontFamily: "'Open Sans', sans-serif", fontWeight: 700, cursor: form.title ? 'pointer' : 'not-allowed',
          }}>{saving ? 'Saving...' : 'Save Project'}</button>
        </div>
      )}

      {projects.length > 0 && (
        <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 8, fontFamily: "'Open Sans', sans-serif", letterSpacing: '0.05em', fontStyle: 'italic' }}>
          Click any field to edit it inline
        </div>
      )}

      {/* Projects table */}
      <div style={{ background: theme.surface, border: `1px solid ${theme.border}` }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr 110px 40px',
          padding: '11px 20px', borderBottom: `1px solid ${theme.border}`,
          fontSize: 10, color: theme.textMuted, letterSpacing: '0.12em',
          textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif",
        }}>
          {[
            { label: 'Project',  field: 'title'    },
            { label: 'Client',   field: 'client'   },
            { label: 'Due Date', field: 'due_date' },
            { label: 'Value',    field: 'value'    },
            { label: 'Notes',    field: 'notes'    },
            { label: 'Status',   field: 'status'   },
          ].map(col => (
            <span
              key={col.field}
              onClick={() => handleSort(col.field)}
              style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 4 }}
            >
              {col.label}
              {sortField === col.field
                ? <span style={{ opacity: 0.8 }}>{sortDir === 'desc' ? ' ↓' : ' ↑'}</span>
                : <span style={{ opacity: 0.25 }}> ↕</span>}
            </span>
          ))}
          <span></span>
        </div>

        {loading && <div style={{ padding: '32px', textAlign: 'center', color: theme.textMuted, fontSize: 13, fontFamily: "'Nunito Sans', sans-serif" }}>Loading projects...</div>}

        {!loading && projects.length === 0 && (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif", marginBottom: 8 }}>No projects yet</div>
            <div style={{ fontSize: 12, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif", opacity: 0.6 }}>Click "+ New Project" to add your first one</div>
          </div>
        )}

        {!loading && sortedProjects.map((p, i) => (
          <div key={p.id} style={{
            display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr 110px 40px',
            padding: theme.rowPadding, borderBottom: i < sortedProjects.length - 1 ? `1px solid ${theme.border}` : 'none',
            alignItems: 'center',
          }}>
            <EditableCell value={p.title} theme={theme} canEdit={canEdit} onSave={v => handleUpdate(p.id, 'title', v)} />
            <EditableCell value={p.client} theme={theme} canEdit={canEdit} onSave={v => handleUpdate(p.id, 'client', v)} />
            <EditableCell value={p.due_date} theme={theme} type="date" canEdit={canEdit} onSave={v => handleUpdate(p.id, 'due_date', v)} />
            <EditableCell value={String(p.value || '')} theme={theme} type="number" isAmount canEdit={canEdit} onSave={v => handleUpdate(p.id, 'value', v)} />
            <EditableCell value={p.notes} theme={theme} canEdit={canEdit} onSave={v => handleUpdate(p.id, 'notes', v)} />
            <EditableCell value={p.status} theme={theme} isStatus statusOptions={statusOptions} statusColors={statusColors} canEdit={canEdit} onSave={v => handleUpdate(p.id, 'status', v)} />
            {canEdit && <button onClick={() => handleDelete(p.id)} style={{ background: 'transparent', border: 'none', color: theme.textMuted, cursor: 'pointer', fontSize: 16, padding: '0 4px', opacity: 0.5 }} title="Delete project">×</button>}
          </div>
        ))}
      </div>
    </div>
  )
}